import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, ArrowLeft, Calendar, Clock, Users, ArrowRight } from "lucide-react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { formatApiError } from "../lib/AuthContext";
import { useT } from "../lib/i18n";
import { validatePhone } from "../lib/validate";
import { PhoneField } from "../components/PhoneField";
import { RevealLines } from "../components/Reveal";
import { MembershipIntakeForm } from "./Home";

const ORDER = [
  "joh_membership_2x",
  "joh_membership_1x",
  "joh_membership_3x",
  "joh_pack_5",
  "joh_pack_10",
  "joh_dropin",
];

const KIND_LABELS = {
  membership: { nl: "Membership", en: "Membership" },
  pack: { nl: "Strippenkaart", en: "Class pack" },
  dropin: { nl: "Drop-in", en: "Drop-in" },
};

const kindLabel = (it, lang) =>
  it.lookup_key === "joh_dropin" ? KIND_LABELS.dropin[lang] : (KIND_LABELS[it.kind]?.[lang] || it.kind);

const priceSuffix = (it, lang) =>
  it.lookup_key === "joh_dropin"
    ? (lang === "en" ? "per session" : "per sessie")
    : it.recurring
      ? (lang === "en" ? `/ ${it.interval}` : `/ maand`)
      : (lang === "en" ? "once" : "eenmalig");

const KIND_COPY = {
  joh_membership_1x: {
    nl: {
      tag: "Start ritme",
      blurb: "1 groepsles per week. Perfect voor een rustige start.",
      bullets: ["1 groepsles per week", "Alle lestypes op het rooster", "Maandelijks opzegbaar"],
    },
    en: {
      tag: "Start rhythm",
      blurb: "1 group class per week. Perfect for a calm start.",
      bullets: ["1 group class per week", "All class types on the schedule", "Cancel monthly"],
    },
  },
  joh_membership_2x: {
    nl: {
      tag: "Meest populair",
      blurb: "2 groepslessen per week. De sweet spot voor consistente progressie.",
      bullets: ["2 groepslessen per week", "Alle lestypes op het rooster", "Maandelijks opzegbaar"],
    },
    en: {
      tag: "Most popular",
      blurb: "2 group classes per week. The sweet spot for consistent progression.",
      bullets: ["2 group classes per week", "All class types on the schedule", "Cancel monthly"],
    },
  },
  joh_membership_3x: {
    nl: {
      tag: "Onbeperkt",
      blurb: "Onbeperkt trainen. Members kunnen gebruik maken van álle lessen op het rooster.",
      bullets: ["Onbeperkt groepslessen per week", "Toegang tot alle lestypes", "Maandelijks opzegbaar"],
    },
    en: {
      tag: "Unlimited",
      blurb: "Unlimited training. Members can join every class on the schedule.",
      bullets: ["Unlimited group classes per week", "Access to every class type", "Cancel monthly"],
    },
  },
  joh_pack_5: {
    nl: {
      tag: "5 sessies · 3 mnd",
      blurb: "5 lessen om te wennen aan het ritme. Geen abonnement.",
      bullets: ["5 groepslessen", "Geldig 3 maanden", "Alle lestypes", "Geen abonnement"],
    },
    en: {
      tag: "5 sessions · 3 months",
      blurb: "5 classes to ease into the rhythm. No subscription.",
      bullets: ["5 group classes", "Valid 3 months", "All class types", "No subscription"],
    },
  },
  joh_pack_10: {
    nl: {
      tag: "10 sessies · 6 mnd",
      blurb: "10 lessen naar keuze. Beste waarde per les.",
      bullets: ["10 groepslessen", "Geldig 6 maanden", "Elke les op het rooster", "Geen abonnement"],
    },
    en: {
      tag: "10 sessions · 6 months",
      blurb: "10 classes to use whenever. Best value per class.",
      bullets: ["10 group classes", "Valid 6 months", "Any class on the schedule", "No subscription"],
    },
  },
  joh_dropin: {
    nl: {
      tag: "1 sessie · eenmalig",
      blurb: "Eén losse les wanneer het jou uitkomt. Geen abonnement.",
      bullets: ["1 groepsles naar keuze", "Eenmalige betaling", "Direct te boeken"],
    },
    en: {
      tag: "1 session · one-off",
      blurb: "One single class whenever it suits you. No subscription.",
      bullets: ["1 group class of choice", "One-off payment", "Book instantly"],
    },
  },
};

function fmtPrice(item) {
  const hasCents = item.amount % 100 !== 0;
  const euro = (item.amount / 100).toLocaleString("nl-NL", {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  });
  return `€${euro}`;
}

/* --------------------- DROP-IN FLOW: registratie → leskeuze → betaling --------------------- */
function DropInFlow({ lang, price, promoCode, onBack }) {
  const [step, setStep] = useState(1); // 1 = registratie, 2 = les kiezen
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", experience: "" });
  const [phoneErr, setPhoneErr] = useState("");
  const [classes, setClasses] = useState(null);
  const [classId, setClassId] = useState("");
  const [paying, setPaying] = useState(false);
  const [waitlisted, setWaitlisted] = useState({}); // instance_id → positie
  const [wlBusy, setWlBusy] = useState("");

  const joinWaitlist = async (c) => {
    setWlBusy(c.id);
    try {
      const { data } = await api.post("/classes/waitlist", {
        instance_id: c.id,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
      });
      setWaitlisted(w => ({ ...w, [c.id]: data.position }));
      toast.success(lang === "en" ? `You're on the waitlist (position ${data.position})` : `Je staat op de wachtlijst (plek ${data.position})`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || (lang === "en" ? "Waitlist failed" : "Wachtlijst mislukt"));
    }
    setWlBusy("");
  };
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/classes/upcoming?days_ahead=21");
        // bedrijfssessies (bv. PROVOCATIVE.) zijn nooit boekbaar — niet in de drop-in flow tonen
        setClasses((data.items || []).filter((c) => c.bookable !== false));
      } catch {
        setClasses([]);
        toast.error(lang === "en" ? "Could not load classes" : "Kon lessen niet laden");
      }
    })();
  }, [lang]);

  const toStep2 = (e) => {
    e.preventDefault();
    const pErr = validatePhone(form.phone, { required: true });
    if (pErr) { setPhoneErr(pErr); return; }
    setStep(2);
  };

  const chosen = (classes || []).find(c => c.id === classId);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "joh_dropin",
        quantity: 1,
        origin_url: window.location.origin,
        email: form.email.trim().toLowerCase(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        instance_id: classId,
        experience: form.experience || undefined,
        promo_code: promoCode || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || (lang === "en" ? "Checkout failed" : "Checkout mislukt"));
      setPaying(false);
    }
  };

  const clsLabel = (c) => new Date(c.date + "T00:00:00").toLocaleDateString(lang === "en" ? "en-GB" : "nl-NL", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div data-testid="dropin-flow">
      {step === 1 ? (
        <form onSubmit={toStep2} className="space-y-6">
          <div className="chip text-[#4A4A4A]">{lang === "en" ? "Step 1 — your details" : "Stap 1 — je gegevens"}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="chip text-[#4A4A4A]">{lang === "en" ? "First name*" : "Voornaam*"}</label>
              <input required value={form.first_name} onChange={set("first_name")} className="editorial-input" data-testid="dropin-first-name"/>
            </div>
            <div>
              <label className="chip text-[#4A4A4A]">{lang === "en" ? "Last name*" : "Achternaam*"}</label>
              <input required value={form.last_name} onChange={set("last_name")} className="editorial-input" data-testid="dropin-last-name"/>
            </div>
            <div>
              <label className="chip text-[#4A4A4A]">{lang === "en" ? "Email*" : "E-mail*"}</label>
              <input required type="email" value={form.email} onChange={set("email")} className="editorial-input" placeholder="jij@voorbeeld.com" data-testid="dropin-email"/>
            </div>
            <div>
              <PhoneField value={form.phone} onChange={(v) => { setForm(f => ({ ...f, phone: v })); setPhoneErr(validatePhone(v, { required: true })); }} error={phoneErr} testid="dropin-phone" label={lang === "en" ? "Phone*" : "Telefoon*"}/>
            </div>
            <div className="md:col-span-2">
              <label className="chip text-[#4A4A4A]">{lang === "en" ? "Experience level*" : "Ervaringsniveau*"}</label>
              <select required value={form.experience} onChange={set("experience")} className="editorial-input" data-testid="dropin-experience">
                <option value="" disabled>{lang === "en" ? "Choose your level" : "Kies je niveau"}</option>
                <option value="beginner">Beginner</option>
                <option value="gemiddeld">{lang === "en" ? "Intermediate" : "Gemiddeld"}</option>
                <option value="gevorderd">{lang === "en" ? "Advanced" : "Gevorderd"}</option>
              </select>
            </div>
          </div>
          <button type="submit" className="btn" data-testid="dropin-to-class">
            {lang === "en" ? "Continue — choose your class" : "Verder — kies je les"} <ArrowRight size={14}/>
          </button>
        </form>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="chip text-[#4A4A4A]">{lang === "en" ? "Step 2 — choose your class" : "Stap 2 — kies je les"}</div>
            <button onClick={() => setStep(1)} className="chip text-[#4A4A4A] hover:text-[#0A0A0A]" data-testid="dropin-back-step1">← {lang === "en" ? "Details" : "Gegevens"}</button>
          </div>
          {classes === null ? (
            <div className="chip text-[#4A4A4A]">{lang === "en" ? "Loading classes…" : "Lessen laden…"}</div>
          ) : classes.length === 0 ? (
            <div className="border border-[#0A0A0A]/15 p-8 text-center chip text-[#4A4A4A]" data-testid="dropin-no-classes">
              {lang === "en" ? "No classes available at the moment." : "Er zijn momenteel geen beschikbare lessen."}
            </div>
          ) : (
            <div className="line-t" data-testid="dropin-class-list">
              {classes.map((c) => {
                const remaining = c.capacity - (c.going || 0);
                const full = remaining <= 0;
                const selected = classId === c.id;
                const wlPos = waitlisted[c.id];
                return (
                  <div key={c.id} className={`line-b transition-colors ${full ? "opacity-60" : ""}`}>
                    <button
                      type="button"
                      disabled={full}
                      onClick={() => setClassId(c.id)}
                      className={`w-full py-4 grid grid-cols-12 gap-3 items-center text-left transition-colors ${full ? "cursor-not-allowed" : "hover:bg-[#0A0A0A]/[0.03]"} ${selected ? "bg-[#0A0A0A] text-[#F5F3F0] px-4" : ""}`}
                      data-testid={`dropin-class-${c.id}`}
                    >
                      <span className="col-span-5 md:col-span-4">
                        <span className={`chip inline-flex items-center gap-2 ${selected ? "text-[#DDD6CB]" : "text-[#4A4A4A]"}`}><Calendar size={12}/> {clsLabel(c)}</span>
                      </span>
                      <span className={`col-span-3 md:col-span-2 chip inline-flex items-center gap-2 ${selected ? "text-[#DDD6CB]" : "text-[#4A4A4A]"}`}><Clock size={12}/> {c.start} — {c.end}</span>
                      <span className="col-span-4 md:col-span-4 font-display uppercase text-xl truncate">{c.type}</span>
                      <span className={`col-span-12 md:col-span-2 chip md:text-right ${full ? "text-red-600" : selected ? "text-[#DDD6CB]" : "text-[#4A4A4A]"}`} data-testid={`dropin-class-remaining-${c.id}`}>
                        {full
                          ? (lang === "en" ? "Full" : "Vol")
                          : c.show_count
                            ? <><Users size={12} className="inline mr-1"/>{remaining} {lang === "en" ? "spots left" : "plekken"}</>
                            : null}
                      </span>
                    </button>
                    {full && (
                      <div className="pb-3 pl-1" data-testid={`dropin-waitlist-wrap-${c.id}`}>
                        {wlPos ? (
                          <span className="chip" style={{ color: "var(--joh-accent)" }} data-testid={`dropin-waitlisted-${c.id}`}>
                            {lang === "en" ? `On the waitlist · position ${wlPos}` : `Op de wachtlijst · plek ${wlPos}`}
                          </span>
                        ) : (
                          <button type="button" onClick={() => joinWaitlist(c)} disabled={wlBusy === c.id} className="chip underline underline-offset-4 text-[#4A4A4A] hover:text-[#0A0A0A]" data-testid={`dropin-waitlist-${c.id}`}>
                            {wlBusy === c.id ? "…" : (lang === "en" ? "Join the waitlist for this class" : "Zet mij op de wachtlijst voor deze les")}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {chosen && (
            <div className="border border-[#0A0A0A] p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid="dropin-summary">
              <div className="flex-1">
                <div className="chip text-[#4A4A4A]">{lang === "en" ? "Your drop-in" : "Jouw drop-in"}</div>
                <div className="font-display uppercase text-2xl mt-1">{chosen.type}</div>
                <div className="chip text-[#4A4A4A] mt-1">{clsLabel(chosen)} · {chosen.start} — {chosen.end}</div>
                <div className="chip text-[#4A4A4A] mt-1">{form.first_name} {form.last_name} · {form.email}</div>
              </div>
              <button onClick={pay} disabled={paying} className="btn" data-testid="dropin-pay">
                {paying ? (lang === "en" ? "Starting payment…" : "Betaling starten…") : (lang === "en" ? `Pay ${price}` : `Betaal ${price}`)} <ArrowUpRight size={14}/>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default function Join() {
  const { t, lang } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(params.get("plan"));
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, kind, percent, packages }
  const [promoBusy, setPromoBusy] = useState(false);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoBusy(true);
    try {
      const { data } = await api.post("/promos/validate", { code: promoInput.trim() });
      if (!data.valid) {
        toast.error(data.reason || (lang === "en" ? "Invalid code" : "Ongeldige code"));
        setAppliedPromo(null);
      } else {
        setAppliedPromo(data);
        toast.success(
          data.kind === "percent"
            ? (lang === "en" ? `${data.percent}% discount applied` : `${data.percent}% korting toegepast`)
            : (lang === "en" ? "Free session code applied" : "Gratis sessie-code toegepast")
        );
      }
    } catch {
      toast.error(lang === "en" ? "Could not validate code" : "Code kon niet gevalideerd worden");
    }
    setPromoBusy(false);
  };

  const promoNote = appliedPromo
    ? appliedPromo.kind === "percent"
      ? (lang === "en" ? `${appliedPromo.percent}% off the first payment` : `${appliedPromo.percent}% korting op de eerste betaling`)
      : appliedPromo.kind === "fixed"
        ? (lang === "en" ? `€${((appliedPromo.amount_cents || 0) / 100).toFixed(0)} off the first payment` : `€${((appliedPromo.amount_cents || 0) / 100).toFixed(0)} korting op de eerste betaling`)
        : (lang === "en" ? "1 free session on top of your purchase" : "1 gratis sessie bovenop je aankoop")
    : null;

  // Discounted price in cents when the applied promo covers this item (null = no change)
  const discountedAmount = (it) => {
    if (!appliedPromo || appliedPromo.kind === "free_session") return null;
    const pkgs = appliedPromo.packages || ["all"];
    if (!pkgs.includes("all") && !pkgs.includes(it.lookup_key)) return null;
    if (appliedPromo.kind === "percent") return Math.round(it.amount * (1 - appliedPromo.percent / 100));
    if (appliedPromo.kind === "fixed") return Math.max(0, it.amount - (appliedPromo.amount_cents || 0));
    return null;
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/catalog");
        const map = Object.fromEntries(data.items.map(i => [i.lookup_key, i]));
        setItems(ORDER.map(k => map[k]).filter(Boolean));
      } catch (e) {
        toast.error(lang === "en" ? "Could not load pricing" : "Kon de prijzen niet laden");
      }
      setLoading(false);
    })();
  }, [lang]);

  // Na "Doorgaan" soepel naar het intake/drop-in-blok scrollen (vooral mobiel belangrijk)
  useEffect(() => {
    if (selectedPlan && (hasIntake(selectedPlan) || selectedPlan === "joh_dropin")) {
      const timer = setTimeout(() => {
        document.getElementById("join-intake")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [selectedPlan]); // eslint-disable-line

  // Deep-link voor drop-in (?plan=joh_dropin, o.a. vanuit het ledenportaal) → open de drop-in flow
  const autoCheckoutRef = useRef(false);
  useEffect(() => {
    const plan = params.get("plan");
    if (plan === "joh_dropin" && items.length && !autoCheckoutRef.current) {
      autoCheckoutRef.current = true;
      setSelectedPlan("joh_dropin");
    }
  }, [items]); // eslint-disable-line

  const isMembership = (k) => k && k.startsWith("joh_membership_");
  // Memberships én strippenkaarten doorlopen eerst het volledige registratie-intake
  const hasIntake = (k) => k && (isMembership(k) || k.startsWith("joh_pack_"));

  const choose = (lookup_key) => {
    if (lookup_key === "joh_dropin") {
      // Drop-in: eigen flow — registratie + leskeuze vóór betaling
      setSelectedPlan("joh_dropin");
      setParams({ plan: lookup_key });
      return;
    }
    if (hasIntake(lookup_key)) {
      setSelectedPlan(lookup_key);
      setParams({ plan: lookup_key });
      return;
    }
    return checkout(lookup_key);
  };

  const checkout = async (lookup_key) => {
    setCheckingOut(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        quantity: 1,
        origin_url: window.location.origin,
        promo_code: appliedPromo?.code || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      // Membership vereist een compleet profiel (spec 5): stuur ingelogde gebruiker eerst naar /profiel
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "profile_incomplete") {
        toast.info(lang === "en" ? "Complete your profile first" : "Vul eerst je profiel aan");
        nav(`/profiel?next=${encodeURIComponent(`/join?plan=${lookup_key}`)}`);
        setCheckingOut(null);
        return;
      }
      toast.error(formatApiError(detail) || (lang === "en" ? "Checkout failed" : "Checkout mislukt"));
      setCheckingOut(null);
    }
  };

  return (
    <main className="min-h-screen bg-[#F5F3F0] grain" data-testid="join-page">
      <header className="px-6 md:px-10 py-5 line-b flex items-center justify-between">
        <Link to="/" className="font-logo text-2xl md:text-3xl text-[#0A0A0A]" data-testid="join-back-home">JOH.</Link>
        <Link to="/" className="chip text-[#4A4A4A] hover:text-[#0A0A0A] inline-flex items-center gap-2" data-testid="join-back-link">
          <ArrowLeft size={14} /> {lang === "en" ? "Back to home" : "Terug naar home"}
        </Link>
      </header>

      <section className="px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-2 chip text-[#4A4A4A]">— Join / Ch. 06.b</div>
          <div className="lg:col-span-10">
            <h1 className="font-display uppercase text-[#0A0A0A] text-[13vw] lg:text-[8.5vw] leading-[0.86]" data-testid="join-title">
              <RevealLines
                lines={[<>Choose <span className="font-serif-italic normal-case text-[#4A4A4A] tracking-tight">your</span></>, "path in."]}
                delay={0.2}
                lineDelay={0.14}
              />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.9 }}
              className="mt-8 text-[#0A0A0A] text-base md:text-lg max-w-xl leading-relaxed"
            >
              {lang === "en"
                ? "One membership, or a class pack when your week is unpredictable. Pick what fits — you can always switch later."
                : "Eén membership, of een strippenkaart wanneer je week onvoorspelbaar is. Kies wat past — je kunt later altijd wisselen."}
            </motion.p>
          </div>
        </div>

        {loading ? (
          <div className="chip text-[#4A4A4A]">{lang === "en" ? "Loading pricing…" : "Prijzen laden…"}</div>
        ) : (
          <>
          {/* Promocode */}
          <div className="mb-10 max-w-xl" data-testid="join-promo-wrap">
            <label className="chip text-[#4A4A4A]">{lang === "en" ? "Promo code?" : "Promocode?"}</label>
            <div className="mt-2 flex gap-2">
              <input
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                placeholder={lang === "en" ? "Enter code" : "Vul je code in"}
                className="editorial-input uppercase flex-1"
                data-testid="join-promo-input"
              />
              <button onClick={applyPromo} disabled={promoBusy || !promoInput.trim()} className="btn" data-testid="join-promo-apply">
                {promoBusy ? "…" : (lang === "en" ? "Apply" : "Toepassen")}
              </button>
            </div>
            {appliedPromo && (
              <div className="mt-3 inline-flex items-center gap-3 border px-4 py-2" style={{ borderColor: "var(--joh-accent)" }} data-testid="join-promo-applied">
                <span className="chip" style={{ color: "var(--joh-accent)" }}>{appliedPromo.code}</span>
                <span className="text-sm text-[#0A0A0A]">{promoNote}</span>
                <button onClick={() => { setAppliedPromo(null); setPromoInput(""); }} className="chip text-[#4A4A4A] hover:text-[#0A0A0A]" data-testid="join-promo-remove">✕</button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
            {items.map((it, i) => {
              const copy = KIND_COPY[it.lookup_key]?.[lang] || KIND_COPY[it.lookup_key]?.nl;
              const highlight = it.lookup_key === "joh_membership_2x";
              return (
                <motion.div
                  key={it.lookup_key}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className={`flex flex-col justify-between border p-6 md:p-8 min-h-[520px] ${
                    highlight
                      ? "bg-[#0A0A0A] text-[#F5F3F0] border-[#0A0A0A]"
                      : "bg-[#EAE7E2] text-[#0A0A0A] border-[#0A0A0A]"
                  }`}
                  data-testid={`plan-${it.lookup_key}`}
                >
                  <div>
                    <div className={`chip ${highlight ? "text-[#DDD6CB]" : "text-[#4A4A4A]"}`}>
                      {kindLabel(it, lang)}
                    </div>
                    <div className="font-display uppercase text-3xl md:text-4xl mt-2 leading-[0.95]">{it.label}</div>
                    <div className="chip mt-4 opacity-70">{copy?.tag}</div>

                    <div className="mt-8 flex items-baseline gap-3">
                      {discountedAmount(it) !== null && (
                        <span className="font-display text-2xl md:text-3xl line-through opacity-40" data-testid={`plan-old-price-${it.lookup_key}`}>{fmtPrice(it)}</span>
                      )}
                      <span className="font-display text-6xl md:text-7xl leading-none" data-testid={`plan-price-${it.lookup_key}`}>
                        {discountedAmount(it) !== null ? fmtPrice({ ...it, amount: discountedAmount(it) }) : fmtPrice(it)}
                      </span>
                      <span className={`chip ${highlight ? "text-[#DDD6CB]" : "text-[#4A4A4A]"}`}>
                        {priceSuffix(it, lang)}
                      </span>
                    </div>

                    <ul className="mt-8 space-y-3">
                      {(copy?.bullets || []).map((b, k) => (
                        <li key={k} className="flex items-start gap-3 text-sm md:text-base">
                          <Check size={16} strokeWidth={1.5} className="mt-1 shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    onClick={() => choose(it.lookup_key)}
                    disabled={checkingOut === it.lookup_key}
                    className={`${highlight ? "btn-light" : "btn"} mt-8 w-full justify-center ${selectedPlan === it.lookup_key ? "ring-2 ring-offset-2" : ""}`}
                    style={selectedPlan === it.lookup_key ? { boxShadow: "0 0 0 2px var(--joh-accent)" } : undefined}
                    data-testid={`checkout-btn-${it.lookup_key}`}
                  >
                    {checkingOut === it.lookup_key
                      ? (lang === "en" ? "Loading…" : "Laden…")
                      : selectedPlan === it.lookup_key && hasIntake(it.lookup_key)
                        ? (lang === "en" ? "Selected · scroll down" : "Geselecteerd · scroll omlaag")
                        : (lang === "en" ? "Continue" : "Doorgaan")}
                    <ArrowUpRight size={14} />
                  </button>
                </motion.div>
              );
            })}

            {/* Personal Training — geen directe betaling, aanvraag via het PT-formulier */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + items.length * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col justify-between border p-6 md:p-8 min-h-[520px] bg-[#EAE7E2] text-[#0A0A0A] border-[#0A0A0A]"
              data-testid="plan-joh_pt"
            >
              <div>
                <div className="chip text-[#4A4A4A]">Personal Training</div>
                <div className="font-display uppercase text-3xl md:text-4xl mt-2 leading-[0.95]">Personal Training</div>
                <div className="chip mt-4 opacity-70">{lang === "en" ? "1-on-1 · by appointment" : "1-op-1 · op afspraak"}</div>
                <div className="mt-8 flex items-baseline gap-3">
                  <span className="font-display text-3xl md:text-4xl leading-none" data-testid="plan-price-joh_pt">Tailored to you</span>
                </div>
                <ul className="mt-8 space-y-3">
                  {(lang === "en"
                    ? ["1-on-1 with the coach", "Your pace, your goals", "Technique, conditioning, strength"]
                    : ["1-op-1 met de coach", "Jouw tempo, jouw doelen", "Techniek, conditie, kracht"]
                  ).map((b, k) => (
                    <li key={k} className="flex items-start gap-3 text-sm md:text-base">
                      <Check size={16} strokeWidth={1.5} className="mt-1 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <a href="/#pt" className="btn mt-8 w-full justify-center" data-testid="enquire-btn-joh_pt">
                {lang === "en" ? "Enquire" : "Vraag aan"} <ArrowUpRight size={14} />
              </a>
            </motion.div>
          </div>
          </>
        )}

        {selectedPlan === "joh_dropin" && (() => {
          const sel = items.find(x => x.lookup_key === "joh_dropin");
          if (!sel) return null;
          const copy = KIND_COPY["joh_dropin"]?.[lang] || KIND_COPY["joh_dropin"]?.nl;
          return (
            <section id="join-intake" className="mt-20 border-t line-t pt-16" data-testid="join-dropin-wrap">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-8">
                <div className="lg:col-span-4">
                  <div className="chip text-[#4A4A4A]">— {lang === "en" ? "Drop-in" : "Drop-in"}</div>
                  <h2 className="font-display uppercase text-4xl md:text-5xl mt-2 text-[#0A0A0A]" data-testid="join-dropin-title">{sel.label}</h2>
                  <div className="mt-4 flex items-baseline gap-3">
                    <span className="font-display text-6xl md:text-7xl text-[#0A0A0A]">{fmtPrice(sel)}</span>
                    <span className="chip text-[#4A4A4A] font-mono">{lang === "en" ? "once" : "eenmalig"}</span>
                  </div>
                  <p className="mt-4 text-[#4A4A4A]">{copy?.blurb}</p>
                  <p className="mt-2 text-sm text-[#4A4A4A]">{lang === "en" ? "Register, pick your class, then pay — your spot is reserved instantly." : "Registreer, kies je les en betaal daarna — je plek is direct gereserveerd."}</p>
                  <button onClick={() => { setSelectedPlan(null); setParams({}); }} className="chip text-[#4A4A4A] hover:text-[#0A0A0A] mt-6 inline-flex items-center gap-1" data-testid="join-dropin-change">
                    ← {lang === "en" ? "Change plan" : "Ander plan kiezen"}
                  </button>
                </div>
                <div className="lg:col-span-8">
                  <DropInFlow
                    lang={lang}
                    price={fmtPrice(sel)}
                    promoCode={appliedPromo?.code || null}
                    onBack={() => { setSelectedPlan(null); setParams({}); }}
                  />
                </div>
              </div>
            </section>
          );
        })()}

        {selectedPlan && hasIntake(selectedPlan) && (() => {
          const sel = items.find(x => x.lookup_key === selectedPlan);
          if (!sel) return null;
          const copy = KIND_COPY[selectedPlan]?.[lang] || KIND_COPY[selectedPlan]?.nl;
          return (
            <section id="join-intake" className="mt-20 border-t line-t pt-16" data-testid="join-intake-wrap">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-8">
                <div className="lg:col-span-4">
                  <div className="chip text-[#4A4A4A]">— {lang === "en" ? "Selected plan" : "Gekozen plan"}</div>
                  <h2 className="font-display uppercase text-4xl md:text-5xl mt-2 text-[#0A0A0A]" data-testid="join-intake-title">{sel.label}</h2>
                  <div className="mt-4 flex items-baseline gap-3">
                    {discountedAmount(sel) !== null && (
                      <span className="font-display text-2xl md:text-3xl line-through opacity-40 text-[#0A0A0A]">{fmtPrice(sel)}</span>
                    )}
                    <span className="font-display text-6xl md:text-7xl text-[#0A0A0A]">
                      {discountedAmount(sel) !== null ? fmtPrice({ ...sel, amount: discountedAmount(sel) }) : fmtPrice(sel)}
                    </span>
                    <span className="chip text-[#4A4A4A] font-mono">{sel.recurring ? (lang === "en" ? "/ month" : "/ maand") : (lang === "en" ? "once" : "eenmalig")}</span>
                  </div>
                  <p className="mt-4 text-[#4A4A4A]">{copy?.blurb}</p>
                  <button onClick={() => { setSelectedPlan(null); setParams({}); }} className="chip text-[#4A4A4A] hover:text-[#0A0A0A] mt-6 inline-flex items-center gap-1" data-testid="join-intake-change">
                    ← {lang === "en" ? "Change plan" : "Ander plan kiezen"}
                  </button>
                </div>
                <div className="lg:col-span-8">
                  <MembershipIntakeForm
                    lookupKey={selectedPlan}
                    lang={lang}
                    planLabel={sel.label}
                    price={fmtPrice(sel)}
                    promoCode={appliedPromo?.code || null}
                    promoNote={promoNote}
                  />
                </div>
              </div>
            </section>
          );
        })()}

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-[#4A4A4A] text-sm max-w-4xl">
          <div>
            <div className="chip mb-2">{lang === "en" ? "Secure" : "Veilig"}</div>
            {lang === "en" ? "Payments handled by Stripe. Cards, iDEAL, Apple Pay & more." : "Betalingen via Stripe. Kaart, iDEAL, Apple Pay & meer."}
          </div>
          <div>
            <div className="chip mb-2">{lang === "en" ? "Cancel anytime" : "Altijd opzegbaar"}</div>
            {lang === "en" ? "Membership renews monthly. Cancel from your Stripe portal." : "Membership vernieuwt maandelijks. Opzeggen kan via je Stripe portal."}
          </div>
          <div>
            <div className="chip mb-2">{lang === "en" ? "Questions?" : "Vragen?"}</div>
            {lang === "en" ? "Book a free trial first — no commitment." : "Kom eerst een gratis proefles doen — geen verplichting."}
          </div>
        </div>
      </section>
    </main>
  );
}
